import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { reportImageSrcWithVersion } from "./GuttenbergImageAndDiff";
import { parseMermaidFlowchart, parseMermaidNode, cleanMermaidLabel, wrapMermaidLabel, layoutMermaidNodes } from "./GuttenbergMermaid";

const chartRenderCache = new Map();

const chartRenderClientVersion = "chart-block-v3";

const CHART_SERIES_COLORS = ["#1b4f8a", "#d47b1f", "#2f8f4e", "#7f4b8b", "#b23b3b", "#47606f"];

function chartRenderVersion(value) {
    let hash = 0;
    const text = String(value ?? "");
    for (let index = 0; index < text.length; index += 1) {
        hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
    }
    return Math.abs(hash).toString(36);
}

export function ChartPreview({ block, projectId, sectionId, editable, measureOnly = false, onSave, onEditFocus, onEditBlur }) {
    const blockSpecSignature = block.specText || JSON.stringify(block.spec ?? {});
    const parsedBlockSpec = parseChartSpec(blockSpecSignature);
    const initialSpec = chartSpecForEditor(parsedBlockSpec.error ? block.spec ?? {} : parsedBlockSpec.spec);
    const [editorSpec, setEditorSpec] = useState(initialSpec);
    const [editorOpen, setEditorOpen] = useState(false);
    const [error, setError] = useState(block.error || "");
    const [renderedPath, setRenderedPath] = useState("");
    const [renderVersion, setRenderVersion] = useState("");
    const [renderError, setRenderError] = useState("");
    const [renderLoading, setRenderLoading] = useState(false);
    const renderRequestRef = useRef(0);
    const renderedPathRef = useRef("");
    useEffect(() => {
        renderedPathRef.current = renderedPath;
    }, [renderedPath]);
    useEffect(() => {
        const parsed = parseChartSpec(blockSpecSignature);
        setEditorSpec(chartSpecForEditor(parsed.error ? {} : parsed.spec));
        setError(block.error || "");
        setRenderError("");
        setRenderLoading(false);
    }, [block.raw, blockSpecSignature, block.error]);
    useEffect(() => {
        if (!editable)
            setEditorOpen(false);
    }, [editable]);
    const spec = editable ? editorSpec : initialSpec;
    const chart = buildChartPreview(spec);
    const title = chartTitle(spec);
    const caption = capitalizeCaption(String(spec.caption ?? title));
    const canRenderLocally = chartHasLocalData(chart);
    const shouldRenderRemotely = !measureOnly && !block.error && Boolean(projectId) && chartNeedsRemoteRender(spec, chart);
    const renderPayload = JSON.stringify(cleanChartSpecForSave(spec));
    const renderKey = `${chartRenderClientVersion}|${projectId || ""}|${sectionId || ""}|${renderPayload}`;
    useEffect(() => {
        if (!shouldRenderRemotely) {
            setRenderLoading(false);
            return undefined;
        }
        const cachedPath = chartRenderCache.get(renderKey);
        if (cachedPath) {
            setRenderedPath(cachedPath);
            setRenderVersion(chartRenderVersion(renderKey));
            setRenderError("");
            setRenderLoading(false);
            return undefined;
        }
        const requestId = renderRequestRef.current + 1;
        renderRequestRef.current = requestId;
        let cancelled = false;
        const isActive = () => !cancelled && renderRequestRef.current === requestId;
        setRenderError("");
        setRenderLoading(true);
        const failTimeoutId = window.setTimeout(() => {
            if (isActive() && !renderedPathRef.current) {
                setRenderLoading(false);
                setRenderError("Graphe non rendu: le rendu prend trop de temps.");
            }
            else if (isActive()) {
                setRenderLoading(false);
            }
        }, 20000);
        const debounceId = window.setTimeout(() => {
            let parsed;
            try {
                parsed = JSON.parse(renderPayload);
            }
            catch {
                if (isActive()) {
                    window.clearTimeout(failTimeoutId);
                    setRenderLoading(false);
                    setRenderError("Graphe non rendu: specification JSON invalide.");
                }
                return;
            }
            api.renderChart(projectId, parsed, sectionId)
            .then((result) => {
            const path = result?.path || "";
            if (path)
                chartRenderCache.set(renderKey, path);
            if (isActive()) {
                window.clearTimeout(failTimeoutId);
                if (path) {
                    renderedPathRef.current = path;
                    setRenderedPath(path);
                    setRenderVersion(`${chartRenderVersion(renderKey)}-${Date.now()}`);
                }
                setRenderLoading(false);
                if (!path && !renderedPathRef.current)
                    setRenderError("Graphe non rendu: aucun fichier genere.");
            }
        })
            .catch(() => {
            if (isActive()) {
                window.clearTimeout(failTimeoutId);
                if (!renderedPathRef.current)
                    setRenderError("Graphe non rendu: donnees insuffisantes.");
                setRenderLoading(false);
            }
        });
        }, 150);
        return () => {
            cancelled = true;
            window.clearTimeout(debounceId);
            window.clearTimeout(failTimeoutId);
        };
    }, [shouldRenderRemotely, projectId, sectionId, renderPayload, renderKey]);
    const baseSpecText = JSON.stringify(cleanChartSpecForSave(initialSpec));
    const editorSpecText = JSON.stringify(cleanChartSpecForSave(editorSpec));
    const editorDirty = baseSpecText !== editorSpecText;
    function saveEditor(nextSpec = editorSpec) {
        onEditBlur?.();
        if (!editable)
            return;
        try {
            const parsed = chartSpecForEditor(nextSpec);
            if (parsed.caption)
                parsed.caption = capitalizeCaption(parsed.caption);
            setError("");
            onSave(parsed);
        }
        catch {
            setError("JSON invalide: le graphe n'a pas ete enregistre.");
        }
    }
    const editorToggle = editable && (<button className="button secondary compact-button chart-editor-toggle" type="button" aria-expanded={editorOpen} onClick={() => setEditorOpen((current) => !current)}>
      <Pencil size={14}/> {editorOpen ? "Replier le graphe" : "Modifier le graphe"}
    </button>);
    const editor = editable && editorOpen && (<ChartEditor spec={editorSpec} dirty={editorDirty} error={error} onChange={(nextSpec) => {
            setError("");
            setEditorSpec(chartSpecForEditor(nextSpec));
        }} onSave={() => saveEditor()} onReset={() => {
            setError("");
            setEditorSpec(initialSpec);
        }} onEditFocus={onEditFocus} onEditBlur={onEditBlur}/>);
    if (block.error) {
        return (<div className="chart-preview chart-preview-error">
        <strong>Graphe non lisible</strong>
        <pre>{block.specText}</pre>
      </div>);
    }
    if (shouldRenderRemotely && renderedPath) {
        return (<figure className="chart-preview">
      {editorToggle}
      <img className="chart-rendered-image" src={reportImageSrcWithVersion(projectId, renderedPath, renderVersion)} alt={caption || title}/>
      {renderLoading && <div className="chart-render-refreshing"><span className="inline-spinner"/> Mise a jour du rendu...</div>}
      {caption && <figcaption>{caption}</figcaption>}
      {editor}
    </figure>);
    }
    if (shouldRenderRemotely && !canRenderLocally) {
        return (<figure className="chart-preview">
      {editorToggle}
      <div className={`chart-render-placeholder ${renderError ? "error" : ""}`}>
        {renderLoading && <span className="inline-spinner"/>}
        <strong>{renderError ? "Graphe non rendu" : "Rendu du graphe"}</strong>
        <p>{renderError || "Preparation de l'aperçu..."}</p>
      </div>
      {caption && <figcaption>{caption}</figcaption>}
      {editor}
    </figure>);
    }
    return (<figure className="chart-preview">
      {editorToggle}
      <svg role="img" aria-label={title} viewBox="0 0 760 420">
        <rect className="chart-frame" height="418" rx="4" width="758" x="1" y="1"/>
        <text className="chart-title" textAnchor="middle" x="380" y="34">{title}</text>
        {["bar", "barh", "histogram"].includes(chart.type)
            ? <ChartBars chart={chart}/>
            : <ChartLines chart={chart}/>}
      </svg>
      {shouldRenderRemotely && renderLoading && <div className="chart-render-refreshing"><span className="inline-spinner"/> Mise a jour du rendu...</div>}
      {caption && <figcaption>{caption}</figcaption>}
      {renderError && <div className="chart-render-error">{renderError}</div>}
      {editor}
    </figure>);
}

function ChartEditor({ spec, dirty, error, onChange, onSave, onReset, onEditFocus, onEditBlur }) {
    const [activeTab, setActiveTab] = useState("series");
    const [jsonDraft, setJsonDraft] = useState(() => JSON.stringify(cleanChartSpecForSave(spec), null, 2));
    const [jsonDirty, setJsonDirty] = useState(false);
    const [jsonError, setJsonError] = useState("");
    const specText = JSON.stringify(cleanChartSpecForSave(spec), null, 2);
    useEffect(() => {
        if (!jsonDirty) {
            setJsonDraft(specText);
            setJsonError("");
        }
    }, [specText, jsonDirty]);
    const series = Array.isArray(spec.series) ? spec.series : [];
    const referenceLines = Array.isArray(spec.reference_lines) ? spec.reference_lines : [];
    function update(patch) {
        onChange({ ...spec, ...patch });
    }
    function updateSeries(index, patch) {
        onChange({
            ...spec,
            series: series.map((item, itemIndex) => (itemIndex === index ? cleanChartObject({ ...item, ...patch }) : item)),
        });
    }
    function removeSeries(index) {
        onChange({ ...spec, series: series.filter((_, itemIndex) => itemIndex !== index) });
    }
    function updateReferenceLine(index, patch) {
        onChange({
            ...spec,
            reference_lines: referenceLines.map((item, itemIndex) => (itemIndex === index ? cleanChartObject({ ...item, ...patch }) : item)),
        });
    }
    function removeReferenceLine(index) {
        onChange({ ...spec, reference_lines: referenceLines.filter((_, itemIndex) => itemIndex !== index) });
    }
    function applyJsonDraft() {
        try {
            const parsed = JSON.parse(jsonDraft);
            onChange(chartSpecForEditor(parsed));
            setJsonDirty(false);
            setJsonError("");
            setActiveTab("series");
        }
        catch {
            setJsonError("JSON invalide.");
        }
    }
    return (<div className="chart-editor-panel" onClick={(event) => event.stopPropagation()}>
      <div className="chart-editor-head">
        <strong>Graphe</strong>
        <div>
          <button className="button secondary compact-button" type="button" onClick={onReset} disabled={!dirty}>
            <RotateCcw size={14}/> Réinitialiser
          </button>
          <button className="button compact-button" type="button" onClick={onSave} disabled={!dirty}>
            <CheckCircle size={14}/> Enregistrer
          </button>
        </div>
      </div>
      <div className="chart-editor-tabs" role="tablist">
        {[
            ["series", "Séries"],
            ["axes", "Axes"],
            ["source", "Source"],
            ["references", "Repères"],
            ["json", "JSON"],
        ].map(([tab, label]) => (<button className={activeTab === tab ? "active" : ""} key={tab} type="button" onClick={() => setActiveTab(tab)}>
            {label}
          </button>))}
      </div>
      {activeTab === "series" && (<div className="chart-editor-section">
          <div className="chart-editor-grid two">
            <ChartTextField label="Titre" value={spec.title ?? ""} onChange={(value) => update({ title: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
            <ChartSelectField label="Type" value={spec.chart_type ?? "line"} onChange={(value) => update({ chart_type: value })} options={[
                ["line", "Courbe"],
                ["scatter", "Nuage"],
                ["bar", "Barres"],
                ["barh", "Barres horizontales"],
            ]}/>
            <ChartTextField label="Légende" value={spec.caption ?? ""} onChange={(value) => update({ caption: value })} onFocus={onEditFocus} onBlur={onEditBlur} wide/>
          </div>
          <div className="chart-series-list">
            {series.map((item, index) => (<div className="chart-series-editor" key={index}>
                <div className="chart-series-head">
                  <strong>{item.label || `Série ${index + 1}`}</strong>
                  <button className="icon-button danger" type="button" title="Supprimer" onClick={() => removeSeries(index)}>
                    <Trash2 size={14}/>
                  </button>
                </div>
                <div className="chart-editor-grid series">
                  <ChartTextField label="Nom" value={item.label ?? ""} onChange={(value) => updateSeries(index, { label: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
                  <ChartTextField label="Colonne" value={item.column ?? item.field ?? item.y_key ?? ""} onChange={(value) => updateSeries(index, { column: value, field: value, y_key: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
                  <ChartColorField label="Couleur" value={chartColorOrDefault(item.color, defaultChartSeriesColor(index))} onChange={(value) => updateSeries(index, { color: value })}/>
                  <ChartSelectField label="Style" value={item.style ?? ""} onChange={(value) => updateSeries(index, { style: value })} options={[
                    ["", "Ligne"],
                    ["scatter", "Points"],
                ]}/>
                  <ChartSelectField label="Axe" value={item.axis ?? ""} onChange={(value) => updateSeries(index, { axis: value })} options={[
                    ["", "Y gauche"],
                    ["y2", "Y droit"],
                ]}/>
                  <ChartSelectField label="Calcul" value={item.transform ?? ""} onChange={(value) => updateSeries(index, { transform: value })} options={[
                    ["", "Aucun"],
                    ["derivative_degC_per_s", "Dérivée °C/s"],
                ]}/>
                  <ChartNumberField label="Ignorer début" value={item.drop_first_points} min={0} step="1" onChange={(value) => updateSeries(index, { drop_first_points: value })}/>
                </div>
                <label className="chart-points-field">
                  <span>Données</span>
                  <textarea value={pointsToText(item.points, { allowCategoricalX: chartAllowsCategoricalX(spec.chart_type ?? spec.type) })} placeholder={chartAllowsCategoricalX(spec.chart_type ?? spec.type) ? "Categorie A, 12\nCategorie B, 18" : "0, 12\n1, 18"} spellCheck="false" onFocus={onEditFocus} onBlur={onEditBlur} onChange={(event) => updateSeries(index, { points: pointsFromText(event.target.value, { allowCategoricalX: chartAllowsCategoricalX(spec.chart_type ?? spec.type) }) })}/>
                </label>
              </div>))}
          </div>
          <button className="button secondary compact-button chart-add-button" type="button" onClick={() => onChange({ ...spec, series: [...series, { label: `Série ${series.length + 1}`, column: "", points: [] }] })}>
            <Plus size={14}/> Ajouter une série
          </button>
        </div>)}
      {activeTab === "axes" && (<div className="chart-editor-section">
          <div className="chart-editor-grid axes">
            <ChartTextField label="Axe X" value={spec.x_label ?? ""} onChange={(value) => update({ x_label: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
            <ChartNumberField label="X min" value={spec.x_min} onChange={(value) => update({ x_min: value })}/>
            <ChartNumberField label="X max" value={spec.x_max} onChange={(value) => update({ x_max: value })}/>
            <ChartTextField label="Axe Y" value={spec.y_label ?? ""} onChange={(value) => update({ y_label: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
            <ChartNumberField label="Y min" value={spec.y_min} onChange={(value) => update({ y_min: value })}/>
            <ChartNumberField label="Y max" value={spec.y_max} onChange={(value) => update({ y_max: value })}/>
            <ChartTextField label="Axe Y droit" value={spec.y2_label ?? ""} onChange={(value) => update({ y2_label: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
            <ChartNumberField label="Y droit min" value={spec.y2_min} onChange={(value) => update({ y2_min: value })}/>
            <ChartNumberField label="Y droit max" value={spec.y2_max} onChange={(value) => update({ y2_max: value })}/>
            <ChartNumberField label="Épaisseur" value={spec.line_width} min={0.5} step="0.5" onChange={(value) => update({ line_width: value })}/>
          </div>
          <div className="chart-toggle-row">
            <label><input type="checkbox" checked={spec.legend !== false} onChange={(event) => update({ legend: event.target.checked })}/> Légende</label>
            <label><input type="checkbox" checked={spec.grid !== false} onChange={(event) => update({ grid: event.target.checked })}/> Grille</label>
            <label><input type="checkbox" checked={spec.points === true} onChange={(event) => update({ points: event.target.checked })}/> Points</label>
          </div>
        </div>)}
      {activeTab === "source" && (<div className="chart-editor-section">
          <div className="chart-editor-grid two">
            <ChartTextField label="Source" value={spec.data_source ?? spec.source_filename ?? ""} onChange={(value) => update({ data_source: value, source_filename: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
            <ChartTextField label="Colonne X" value={spec.x_column ?? ""} onChange={(value) => update({ x_column: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
            <ChartSelectField label="Transformation X" value={spec.x_transform ?? ""} onChange={(value) => update({ x_transform: value })} options={[
                ["", "Aucune"],
                ["elapsed_from_first_ms", "ms depuis t0"],
                ["elapsed_seconds", "s depuis t0"],
                ["ms_to_s", "ms vers s"],
            ]}/>
            <ChartTextField label="Chemin" value={spec.source_path ?? ""} onChange={(value) => update({ source_path: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
          </div>
        </div>)}
      {activeTab === "references" && (<div className="chart-editor-section">
          <div className="chart-series-list">
            {referenceLines.map((line, index) => (<div className="chart-series-editor compact" key={index}>
                <div className="chart-editor-grid reference">
                  <ChartSelectField label="Axe" value={line.axis ?? "y"} onChange={(value) => updateReferenceLine(index, { axis: value })} options={[
                    ["y", "Y"],
                    ["x", "X"],
                ]}/>
                  <ChartNumberField label="Valeur" value={line.value} onChange={(value) => updateReferenceLine(index, { value })}/>
                  <ChartTextField label="Libellé" value={line.label ?? ""} onChange={(value) => updateReferenceLine(index, { label: value })} onFocus={onEditFocus} onBlur={onEditBlur}/>
                  <ChartSelectField label="Style" value={line.style ?? "dashed"} onChange={(value) => updateReferenceLine(index, { style: value })} options={[
                    ["dashed", "Tiret"],
                    ["solid", "Continu"],
                ]}/>
                  <ChartColorField label="Couleur" value={line.color ?? "#64748b"} onChange={(value) => updateReferenceLine(index, { color: value })}/>
                  <button className="icon-button danger" type="button" title="Supprimer" onClick={() => removeReferenceLine(index)}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>))}
          </div>
          <button className="button secondary compact-button chart-add-button" type="button" onClick={() => onChange({ ...spec, reference_lines: [...referenceLines, { axis: "y", value: 0, label: "", style: "dashed" }] })}>
            <Plus size={14}/> Ajouter un repère
          </button>
        </div>)}
      {activeTab === "json" && (<div className="chart-editor-section">
          <textarea className="chart-json-editor" value={jsonDraft} spellCheck="false" onFocus={onEditFocus} onBlur={onEditBlur} onChange={(event) => {
                setJsonDraft(event.target.value);
                setJsonDirty(true);
                setJsonError("");
            }}/>
          <div className="chart-json-actions">
            <button className="button secondary compact-button" type="button" onClick={() => {
                setJsonDraft(specText);
                setJsonDirty(false);
                setJsonError("");
            }}>
              <RotateCcw size={14}/> Recharger
            </button>
            <button className="button compact-button" type="button" onClick={applyJsonDraft}>
              <CheckCircle size={14}/> Appliquer JSON
            </button>
          </div>
          {jsonError && <span className="chart-editor-error">{jsonError}</span>}
        </div>)}
      {error && <span className="chart-editor-error">{error}</span>}
    </div>);
}

function ChartTextField({ label, value, onChange, onFocus, onBlur, wide = false }) {
    return (<label className={`chart-field ${wide ? "wide" : ""}`}>
      <span>{label}</span>
      <input type="text" value={value} onFocus={onFocus} onBlur={onBlur} onChange={(event) => onChange(event.target.value)}/>
    </label>);
}

function ChartNumberField({ label, value, onChange, min, step = "any" }) {
    return (<label className="chart-field">
      <span>{label}</span>
      <input type="number" min={min} step={step} value={chartInputValue(value)} onChange={(event) => onChange(optionalNumber(event.target.value))}/>
    </label>);
}

function ChartSelectField({ label, value, onChange, options }) {
    return (<label className="chart-field">
      <span>{label}</span>
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, optionLabel]) => (<option key={optionValue} value={optionValue}>{optionLabel}</option>))}
      </select>
    </label>);
}

function ChartColorField({ label, value, onChange }) {
    const color = chartColorOrDefault(value, "#1b4f8a");
    return (<label className="chart-field color">
      <span>{label}</span>
      <input type="color" value={color} onChange={(event) => onChange(event.target.value)}/>
    </label>);
}

function ChartLines({ chart }) {
    return (<>
      <ChartAxes chart={chart}/>
      <ChartReferenceLines chart={chart}/>
      {chart.series.map((series, index) => {
            const color = chartColorOrDefault(series.color, defaultChartSeriesColor(index));
            if (chart.type === "scatter" || series.style === "scatter") {
                return (<g key={index}>
            {series.points.map((point, pointIndex) => (<circle cx={chart.x(point.x)} cy={chart.y(point.y)} fill={color} key={pointIndex} r="4"/>))}
          </g>);
            }
            const points = series.points.map((point) => `${chart.x(point.x)},${chart.y(point.y)}`).join(" ");
            return <polyline fill="none" key={index} points={points} stroke={color} strokeWidth="3"/>;
        })}
      {chart.series.length > 1 && <ChartLegend series={chart.series}/>}
    </>);
}

function ChartReferenceLines({ chart }) {
    if (!Array.isArray(chart.referenceLines) || !chart.referenceLines.length)
        return null;
    const plot = chart.plot;
    return (<g className="chart-reference-lines">
      {chart.referenceLines.map((line, index) => {
            const value = numeric(line?.value);
            if (!Number.isFinite(value))
                return null;
            const axis = String(line?.axis ?? "y").toLowerCase();
            const color = chartColorOrDefault(line?.color, "#64748b");
            const label = String(line?.label ?? "");
            const dash = ["dashed", "dash", "--"].includes(String(line?.style ?? "").toLowerCase()) ? "7 5" : "3 4";
            if (axis === "x") {
                if (value < chart.xMin || value > chart.xMax)
                    return null;
                const x = chart.x(value);
                return (<g key={index}>
            <line stroke={color} strokeDasharray={dash} x1={x} x2={x} y1={plot.top} y2={plot.top + plot.height}/>
            {label && <text fill={color} textAnchor="end" transform={`rotate(-90 ${x - 5} ${plot.top + 8})`} x={x - 5} y={plot.top + 8}>{label}</text>}
          </g>);
            }
            if (value < chart.yMin || value > chart.yMax)
                return null;
            const y = chart.y(value);
            return (<g key={index}>
          <line stroke={color} strokeDasharray={dash} x1={plot.left} x2={plot.left + plot.width} y1={y} y2={y}/>
          {label && <text fill={color} textAnchor="end" x={plot.left + plot.width - 6} y={y - 5}>{label}</text>}
        </g>);
        })}
    </g>);
}

function ChartBars({ chart }) {
    const plot = chart.plot;
    const max = Math.max(1, ...chart.bars.map((bar) => Math.abs(bar.value)));
    if (chart.type === "barh") {
        const gap = 9;
        const height = Math.max(12, (plot.height - gap * Math.max(0, chart.bars.length - 1)) / Math.max(1, chart.bars.length));
        return (<>
        <ChartAxes chart={chart} categorical/>
        {chart.bars.map((bar, index) => {
                const width = (Math.abs(bar.value) / max) * plot.width;
                const y = plot.top + index * (height + gap);
                const color = chartColorOrDefault(bar.color, defaultChartSeriesColor(index));
                return (<g key={index}>
              <rect fill={color} height={height} width={width} x={plot.left} y={y}/>
              <text className="chart-bar-label" x={plot.left - 8} y={y + height * 0.68}>{bar.label}</text>
              <text className="chart-value-label" x={plot.left + width + 6} y={y + height * 0.68}>{formatChartNumber(bar.value)}</text>
            </g>);
            })}
      </>);
    }
    const gap = 10;
    const width = Math.max(14, (plot.width - gap * Math.max(0, chart.bars.length - 1)) / Math.max(1, chart.bars.length));
    return (<>
      <ChartAxes chart={chart} categorical/>
      {chart.bars.map((bar, index) => {
            const height = (Math.abs(bar.value) / max) * plot.height;
            const x = plot.left + index * (width + gap);
            const y = plot.top + plot.height - height;
            const color = chartColorOrDefault(bar.color, defaultChartSeriesColor(index));
            return (<g key={index}>
          <rect fill={color} height={height} width={width} x={x} y={y}/>
          <text className="chart-value-label" textAnchor="middle" x={x + width / 2} y={y - 6}>{formatChartNumber(bar.value)}</text>
          <text className="chart-x-label" textAnchor="end" transform={`rotate(-35 ${x + width / 2} ${plot.top + plot.height + 22})`} x={x + width / 2} y={plot.top + plot.height + 22}>{bar.label}</text>
        </g>);
        })}
    </>);
}

function ChartAxes({ chart, categorical = false }) {
    const plot = chart.plot;
    const ticks = [0, 0.25, 0.5, 0.75, 1];
    return (<g>
      <line className="chart-axis" x1={plot.left} x2={plot.left} y1={plot.top} y2={plot.top + plot.height}/>
      <line className="chart-axis" x1={plot.left} x2={plot.left + plot.width} y1={plot.top + plot.height} y2={plot.top + plot.height}/>
      {!categorical && ticks.map((tick) => {
            const y = plot.top + plot.height - tick * plot.height;
            const value = chart.yMin + tick * (chart.yMax - chart.yMin);
            return (<g key={`y-${tick}`}>
            <line className="chart-grid" x1={plot.left} x2={plot.left + plot.width} y1={y} y2={y}/>
            <text className="chart-tick" x={plot.left - 8} y={y + 4}>{formatChartNumber(value)}</text>
          </g>);
        })}
      {!categorical && ticks.map((tick) => {
            const x = plot.left + tick * plot.width;
            const value = chart.xMin + tick * (chart.xMax - chart.xMin);
            return <text className="chart-tick chart-x-tick" key={`x-${tick}`} textAnchor="middle" x={x} y={plot.top + plot.height + 22}>{formatChartNumber(value)}</text>;
        })}
      <text className="chart-axis-title" textAnchor="middle" x={plot.left + plot.width / 2} y="396">{chart.xLabel}</text>
      <text className="chart-axis-title" textAnchor="middle" transform="rotate(-90 18 220)" x="18" y="220">{chart.yLabel}</text>
    </g>);
}

function ChartLegend({ series }) {
    return (<g className="chart-legend" transform="translate(570 62)">
      {series.slice(0, 6).map((item, index) => (<g key={index} transform={`translate(0 ${index * 20})`}>
          <line stroke={chartColorOrDefault(item.color, defaultChartSeriesColor(index))} strokeWidth="3" x1="0" x2="22" y1="0" y2="0"/>
          <text x="30" y="4">{item.label}</text>
        </g>))}
    </g>);
}

function defaultChartSeriesColor(index) {
    return CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length];
}

function chartColorOrDefault(value, fallback) {
    return /^#[0-9a-f]{6}$/i.test(String(value || "")) ? String(value) : fallback;
}

function buildChartPreview(spec) {
    spec = normalizeEditableChartSpec(spec);
    const type = normalizeChartType(spec.chart_type ?? spec.type);
    const plot = { left: 86, top: 56, width: 610, height: 292 };
    if (["bar", "barh", "histogram"].includes(type)) {
        return {
            type,
            plot,
            bars: extractChartBars(spec),
            xLabel: String(spec.x_label ?? spec.x_column ?? ""),
            yLabel: String(spec.y_label ?? spec.y_column ?? "Valeur")
        };
    }
    const rawSeries = extractChartSeries(spec);
    const points = rawSeries.flatMap((item) => item.points);
    const xRange = axisRange(spec, "x", points.map((point) => point.x));
    const xFilteredSeries = filterChartSeriesToRange(rawSeries, xRange, null);
    const yRange = axisRange(spec, "y", xFilteredSeries.flatMap((item) => item.points.map((point) => point.y)));
    const series = filterChartSeriesToRange(xFilteredSeries, xRange, yRange);
    return {
        type,
        plot,
        series,
        referenceLines: extractChartReferenceLines(spec),
        xMin: xRange[0],
        xMax: xRange[1],
        yMin: yRange[0],
        yMax: yRange[1],
        xLabel: String(spec.x_label ?? spec.x_column ?? "x"),
        yLabel: String(spec.y_label ?? spec.y_column ?? "Valeur"),
        x: (value) => plot.left + ((value - xRange[0]) / Math.max(1e-9, xRange[1] - xRange[0])) * plot.width,
        y: (value) => plot.top + plot.height - ((value - yRange[0]) / Math.max(1e-9, yRange[1] - yRange[0])) * plot.height
    };
}

function chartNeedsRemoteRender(spec, chart) {
    if (Array.isArray(spec?.series) && spec.series.some((item) => chartSeriesUsesRightAxis(item)))
        return true;
    if (chartHasLocalData(chart))
        return false;
    const data = spec?.data && typeof spec.data === "object" && !Array.isArray(spec.data) ? spec.data : {};
    const dataSource = spec?.dataSource && typeof spec.dataSource === "object" && !Array.isArray(spec.dataSource) ? spec.dataSource : {};
    const hasSource = Boolean(spec?.data_source || spec?.source_file_id || spec?.source_path || data.source || dataSource.file || dataSource.source || dataSource.path);
    const hasColumnSeries = Array.isArray(spec?.series) && spec.series.some((item) => item?.column || item?.field || item?.y_key || item?.y_column);
    return hasSource && hasColumnSeries;
}

function chartSeriesUsesRightAxis(series) {
    const axis = String(series?.axis ?? series?.y_axis ?? "").trim().toLowerCase();
    return ["y2", "right", "droite", "secondary", "secondaire"].includes(axis);
}

function chartHasLocalData(chart) {
    if (Array.isArray(chart?.bars) && chart.bars.length)
        return true;
    return Array.isArray(chart?.series) && chart.series.some((series) => Array.isArray(series.points) && series.points.length);
}

function extractChartSeries(spec) {
    const rawSeries = Array.isArray(spec.series) ? spec.series : [];
    const series = rawSeries.map((item, index) => {
        const points = chartPointsFromSeries(item);
        const drop = Math.max(0, Number(item?.drop_first_points ?? spec.drop_first_points ?? 0) || 0);
        return {
            label: String(item?.label ?? item?.name ?? `Serie ${index + 1}`),
            color: String(item?.color ?? ""),
            style: String(item?.style ?? ""),
            points: points.slice(drop)
        };
    }).filter((item) => item.points.length);
    if (series.length)
        return series;
    if (Array.isArray(spec.data) && spec.data.length && typeof spec.data[0] === "object") {
        const xKey = String(spec.x_key ?? spec.x_column ?? "x");
        const yDescriptors = rawSeries.length
            ? rawSeries.map((item) => ({
                key: item?.column ?? item?.y_key ?? item?.y_column ?? item?.label,
                label: item?.label ?? item?.name ?? item?.column ?? item?.y_key
            }))
            : (Array.isArray(spec.y_keys) ? spec.y_keys : Array.isArray(spec.y_columns) ? spec.y_columns : [spec.y_key ?? spec.y_column ?? "y"])
                .map((key) => ({ key, label: key }));
        return yDescriptors.map((descriptor) => {
            const key = descriptor.key;
            const points = spec.data.map((row) => ({
                x: numeric(row?.[xKey]),
                y: numeric(row?.[key])
            })).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
            return { label: String(descriptor.label ?? key), points };
        }).filter((item) => item.points.length);
    }
    return [];
}

function chartPointsFromSeries(item) {
    if (!item || typeof item !== "object")
        return [];
    if (Array.isArray(item.points)) {
        return item.points.map((point) => {
            if (Array.isArray(point))
                return { x: numeric(point[0]), y: numeric(point[1]) };
            return { x: numeric(point?.x), y: numeric(point?.y ?? point?.value) };
        }).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
    }
    if (Array.isArray(item.x) && Array.isArray(item.y)) {
        return item.x.map((xValue, index) => ({ x: numeric(xValue), y: numeric(item.y[index]) }))
            .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
    }
    return [];
}

function extractChartBars(spec) {
    const data = Array.isArray(spec.data) ? spec.data : [];
    const bars = data.map((row, index) => {
        if (row && typeof row === "object") {
            return {
                label: String(row.label ?? row.category ?? row.name ?? row.x ?? `Item ${index + 1}`),
                value: numeric(row.value ?? row.y ?? row.count)
            };
        }
        return { label: `Item ${index + 1}`, value: numeric(row) };
    }).filter((item) => Number.isFinite(item.value));
    if (bars.length)
        return bars.slice(0, 30);
    const seriesBars = chartBarsFromSeries(spec);
    if (seriesBars.length)
        return seriesBars.slice(0, 30);
    const values = Array.isArray(spec.values) ? spec.values.map(numeric).filter(Number.isFinite) : [];
    if (normalizeChartType(spec.chart_type ?? spec.type) === "histogram" && values.length) {
        const bins = Math.min(12, Math.max(4, Math.round(Math.sqrt(values.length))));
        const min = Math.min(...values);
        const max = Math.max(...values);
        const width = Math.max(1e-9, (max - min) / bins);
        const counts = Array.from({ length: bins }, () => 0);
        values.forEach((value) => {
            const bucket = Math.min(bins - 1, Math.floor((value - min) / width));
            counts[bucket] += 1;
        });
        return counts.map((value, index) => ({ label: formatChartNumber(min + index * width), value }));
    }
    return [];
}

function chartBarsFromSeries(spec) {
    const rawSeries = Array.isArray(spec.series) ? spec.series.filter((item) => item && typeof item === "object") : [];
    const multipleSeries = rawSeries.length > 1;
    return rawSeries.flatMap((series) => {
        const seriesLabel = String(series.label ?? series.name ?? "").trim();
        return chartBarPointsFromSeries(series).map(({ label, value }) => {
            const cleanLabel = String(label ?? "").trim();
            return {
                label: multipleSeries && seriesLabel && !cleanLabel.includes(seriesLabel)
                    ? `${seriesLabel} - ${cleanLabel}`
                    : cleanLabel,
                value: numeric(value)
            };
        });
    }).filter((item) => item.label && Number.isFinite(item.value));
}

function chartBarPointsFromSeries(series) {
    if (Array.isArray(series.points)) {
        return series.points.map((point) => {
            if (Array.isArray(point))
                return { label: point[0], value: point[1] };
            if (point && typeof point === "object")
                return {
                    label: point.x ?? point.category ?? point.label ?? point.name,
                    value: point.y ?? point.value ?? point.count
                };
            return { label: "", value: NaN };
        });
    }
    if (Array.isArray(series.x) && Array.isArray(series.y))
        return series.x.map((label, index) => ({ label, value: series.y[index] }));
    return [];
}

function axisRange(spec, axis, values) {
    const axisSpec = spec.axes && typeof spec.axes === "object" && !Array.isArray(spec.axes)
        ? spec.axes[axis]
        : null;
    const direct = Array.isArray(spec[`${axis}_range`]) ? spec[`${axis}_range`] : null;
    const minValue = numeric(spec[`${axis}_min`] ?? spec[`${axis}Min`] ?? axisSpec?.min ?? direct?.[0]);
    const maxValue = numeric(spec[`${axis}_max`] ?? spec[`${axis}Max`] ?? axisSpec?.max ?? direct?.[1]);
    const finite = values.filter(Number.isFinite);
    if (Number.isFinite(minValue) || Number.isFinite(maxValue)) {
        const dataMin = finite.length ? Math.min(...finite) : 0;
        const dataMax = finite.length ? Math.max(...finite) : 1;
        const min = Number.isFinite(minValue) ? minValue : dataMin;
        const max = Number.isFinite(maxValue) ? maxValue : dataMax;
        if (Number.isFinite(min) && Number.isFinite(max) && max !== min)
            return [min, max];
    }
    if (!finite.length)
        return [0, 1];
    const min = Math.min(...finite);
    const max = Math.max(...finite);
    if (min === max)
        return [min - 1, max + 1];
    const pad = (max - min) * 0.06;
    return [min - pad, max + pad];
}

function filterChartSeriesToRange(series, xRange, yRange) {
    const hasX = Array.isArray(xRange) && Number.isFinite(xRange[0]) && Number.isFinite(xRange[1]);
    const hasY = Array.isArray(yRange) && Number.isFinite(yRange[0]) && Number.isFinite(yRange[1]);
    if (!hasX && !hasY)
        return series;
    return series.map((item) => ({
        ...item,
        points: item.points.filter((point) => {
            if (hasX && (point.x < xRange[0] || point.x > xRange[1]))
                return false;
            if (hasY && (point.y < yRange[0] || point.y > yRange[1]))
                return false;
            return true;
        })
    })).filter((item) => item.points.length);
}

function extractChartReferenceLines(spec) {
    const lines = Array.isArray(spec.reference_lines)
        ? spec.reference_lines
        : Array.isArray(spec.referenceLines)
            ? spec.referenceLines
            : [];
    return lines.map((line) => ({
        ...line,
        axis: String(line?.axis ?? "y"),
        value: numeric(line?.value),
        label: String(line?.label ?? "")
    })).filter((line) => Number.isFinite(line.value));
}

function normalizeChartType(value) {
    const type = String(value ?? "line").toLowerCase();
    if (["bar_value", "bar_count", "barh"].includes(type))
        return "barh";
    if (["multi_line", "courbe", "graph", "graphe"].includes(type))
        return "line";
    if (type === "hist")
        return "histogram";
    return type;
}

function numeric(value) {
    if (typeof value === "number")
        return Number.isFinite(value) ? value : NaN;
    const parsed = Number(String(value ?? "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : NaN;
}

function formatChartNumber(value) {
    if (!Number.isFinite(value))
        return "";
    if (Math.abs(value) >= 1000 || (Math.abs(value) > 0 && Math.abs(value) < 0.01))
        return value.toExponential(1);
    return Number(value.toFixed(2)).toString();
}

function chartTitle(spec) {
    return String(spec?.title ?? spec?.caption ?? "Graphe").trim() || "Graphe";
}

function inputQueueCommentLabel(comment) {
    if (comment.target_type === "chart")
        return "Graphe a valider";
    if (comment.target_type === "image_edit")
        return "Retouche image";
    if (comment.target_type === "insertion")
        return "Insertion proposee";
    if (comment.target_type === "image")
        return "Commentaire image";
    return "Modification proposee";
}

function cssEscape(value) {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function")
        return CSS.escape(String(value));
    return String(value).replace(/["\\]/g, "\\$&");
}

function chartCommentTargetText(block) {
    const raw = String(block?.raw ?? "").trim();
    if (raw)
        return raw;
    if (block?.spec)
        return chartBlockToMarkdown(block.spec);
    return chartTitle(block?.spec);
}

function chartSourcePath(spec) {
    return String(spec?.source_figure_path ?? spec?.source_path ?? spec?.figure_path ?? spec?.path ?? "").trim();
}











function parseMarkdownBlocks(markdown) {
    const lines = markdown.split("\n");
    const blocks = [];
    let index = 0;
    while (index < lines.length) {
        const line = lines[index].trim();
        if (!line || line.startsWith("<!--") || line.startsWith("[evd_")) {
            index += 1;
            continue;
        }
        const startLine = index;
        const codeFence = line.match(/^```([\w-]+)?\s*$/);
        if (codeFence) {
            const language = (codeFence[1] ?? "").toLowerCase();
            const codeLines = [];
            index += 1;
            while (index < lines.length && !lines[index].trim().startsWith("```")) {
                codeLines.push(lines[index]);
                index += 1;
            }
            const endLine = Math.min(index, lines.length - 1);
            if (language === "mermaid") {
                blocks.push(withBlockMeta({ type: "mermaid", code: codeLines.join("\n").trim() }, lines, startLine, endLine));
            }
            else if (language === "chart" || language === "guttenberg-chart") {
                const specText = codeLines.join("\n").trim();
                const parsed = parseChartSpec(specText);
                blocks.push(withBlockMeta({ type: "chart", spec: parsed.spec, specText, error: parsed.error }, lines, startLine, endLine));
            }
            else if (codeLines.join("\n").trim()) {
                blocks.push(withBlockMeta({ type: "paragraph", text: codeLines.join("\n").trim() }, lines, startLine, endLine));
            }
            index += 1;
            continue;
        }
        if (line === "\\[" || (line.startsWith("\\[") && !line.endsWith("\\]"))) {
            const mathLines = line === "\\[" ? [] : [line.slice(2).trim()];
            index += 1;
            while (index < lines.length && lines[index].trim() !== "\\]") {
                mathLines.push(lines[index]);
                index += 1;
            }
            blocks.push(withBlockMeta({ type: "math_block", latex: mathLines.join("\n").trim() }, lines, startLine, index));
            index += 1;
            continue;
        }
        if (line.startsWith("\\[") && line.endsWith("\\]") && line.length > 4) {
            blocks.push(withBlockMeta({ type: "math_block", latex: line.slice(2, -2).trim() }, lines, startLine, index));
            index += 1;
            continue;
        }
        const looseChart = parseLooseChartBlock(lines, index);
        if (looseChart) {
            blocks.push(withBlockMeta({ type: "chart", spec: looseChart.spec, specText: looseChart.specText, error: looseChart.error }, lines, startLine, looseChart.endLine));
            index = looseChart.endLine + 1;
            continue;
        }
        if (line.startsWith("# ")) {
            blocks.push(withBlockMeta({ type: "h1", text: line.slice(2) }, lines, startLine, index));
            index += 1;
            continue;
        }
        if (line.startsWith("## ")) {
            blocks.push(withBlockMeta({ type: "h2", text: line.slice(3) }, lines, startLine, index));
            index += 1;
            continue;
        }
        const chartLine = parseChartLine(line);
        if (chartLine) {
            blocks.push(withBlockMeta({ type: "chart", ...chartLine }, lines, startLine, index));
            index += 1;
            continue;
        }
        const visualAsset = parseVisualAssetLine(line);
        if (visualAsset) {
            blocks.push(withBlockMeta({ type: "visual_asset", ...visualAsset }, lines, startLine, index));
            index += 1;
            continue;
        }
        const visualRequest = parseVisualRequestLine(line);
        if (visualRequest) {
            blocks.push(withBlockMeta({ type: "visual_request", ...visualRequest }, lines, startLine, index));
            index += 1;
            continue;
        }
        const image = line.match(/^!\[(.*)\]\((.*)\)$/);
        if (image) {
            const caption = lines[index + 1]?.trim().match(/^\*([^*].*?)\*$/);
            const endLine = caption ? index + 1 : index;
            blocks.push(withBlockMeta({ type: "image", alt: image[1], path: image[2], caption: caption?.[1] ?? "" }, lines, startLine, endLine));
            if (caption) {
                index += 2;
                continue;
            }
            index += 1;
            continue;
        }
        if (isMarkdownTableStart(lines, index)) {
            const headers = splitTableRow(lines[index].trim());
            const alignments = parseTableAlignments(lines[index + 1].trim(), headers.length);
            index += 2;
            const rows = [];
            while (index < lines.length && isTableRow(lines[index].trim())) {
                rows.push(splitTableRow(lines[index].trim()));
                index += 1;
            }
            blocks.push(withBlockMeta(normalizeTableBlock({ type: "table", headers, rows, alignments }), lines, startLine, index - 1));
            continue;
        }
        if (line.startsWith("- ")) {
            blocks.push(withBlockMeta({ type: "bullet", text: line.slice(2) }, lines, startLine, index));
            index += 1;
            continue;
        }
        if (isOrderedListItem(line)) {
            const items = [];
            while (index < lines.length && isOrderedListItem(lines[index].trim())) {
                const firstLine = lines[index].trim().replace(/^\d+\.\s+/, "").trim();
                index += 1;
                const bodyLines = [];
                while (index < lines.length &&
                    !isOrderedListItem(lines[index].trim()) &&
                    !isBlockBoundary(lines, index)) {
                    bodyLines.push(lines[index].trim());
                    index += 1;
                }
                const strongTitle = firstLine.match(/^\*\*(.+?)\*\*\.?$/) ?? firstLine.match(/^\*\*(.+?)\*\*\s*(.*)$/);
                if (strongTitle) {
                    items.push({
                        title: strongTitle[1].trim(),
                        body: [strongTitle[2] ?? "", ...bodyLines].join(" ").trim()
                    });
                }
                else {
                    items.push({ title: "", body: [firstLine, ...bodyLines].join(" ").trim() });
                }
            }
            blocks.push(withBlockMeta({ type: "ordered_list", items }, lines, startLine, index - 1));
            continue;
        }
        if (line.startsWith("> ") || line === ">") {
            const quoteLines = [];
            while (index < lines.length && (lines[index].trim().startsWith("> ") || lines[index].trim() === ">")) {
                const content = lines[index].trim();
                quoteLines.push(content === ">" ? "" : content.slice(2));
                index += 1;
            }
            blocks.push(withBlockMeta({ type: "blockquote", lines: quoteLines }, lines, startLine, index - 1));
            continue;
        }
        blocks.push(withBlockMeta({ type: "paragraph", text: line }, lines, startLine, index));
        index += 1;
    }
    return blocks;
}

function withBlockMeta(block, lines, startLine, endLine) {
    const raw = lines.slice(startLine, endLine + 1).join("\n").trim();
    return {
        ...block,
        raw,
        startLine,
        endLine,
        fingerprint: `${block.type}:${blockComparableText(block).toLowerCase().replace(/\s+/g, " ").slice(0, 500)}`
    };
}

function blockComparableText(block) {
    if ("text" in block)
        return block.text;
    if (block.type === "math_block")
        return block.latex;
    if (block.type === "mermaid")
        return block.code;
    if (block.type === "chart")
        return chartTitle(block.spec);
    if (block.type === "image")
        return block.caption || block.path;
    if (block.type === "table")
        return [...block.headers, ...block.rows.flat()].join(" ");
    if (block.type === "ordered_list")
        return block.items.map((item) => `${item.title} ${item.body}`).join(" ");
    if (block.type === "visual_request")
        return `${block.title} ${block.description}`;
    if (block.type === "visual_asset")
        return `${block.caption} ${block.path}`;
    return block.lines.join(" ");
}

function isOrderedListItem(line) {
    return /^\d+\.\s+/.test(line);
}

function isBlockBoundary(lines, index) {
    const line = lines[index].trim();
    return (line.startsWith("# ") ||
        line.startsWith("## ") ||
        line.startsWith("- ") ||
        line.startsWith("```") ||
        Boolean(parseChartLine(line)) ||
        isLooseChartStart(lines, index) ||
        Boolean(parseVisualAssetLine(line)) ||
        Boolean(parseVisualRequestLine(line)) ||
        /^!\[(.*)\]\((.*)\)$/.test(line) ||
        isMarkdownTableStart(lines, index));
}

function parseChartLine(line) {
    const match = line.match(/^\[\[CHART:?\s*(\{.*\})\s*\]\]$/);
    if (!match)
        return null;
    const specText = match[1].trim();
    const parsed = parseChartSpec(specText);
    return { spec: parsed.spec, specText, error: parsed.error };
}

function parseLooseChartBlock(lines, index) {
    if (!isLooseChartStart(lines, index))
        return null;
    let cursor = index + 1;
    while (cursor < lines.length && !lines[cursor].trim())
        cursor += 1;
    const jsonLines = [];
    let depth = 0;
    let inString = false;
    let escaped = false;
    let started = false;
    for (; cursor < lines.length; cursor += 1) {
        const rawLine = lines[cursor];
        jsonLines.push(rawLine);
        const text = `${rawLine}\n`;
        for (const char of text) {
            if (inString) {
                if (escaped)
                    escaped = false;
                else if (char === "\\")
                    escaped = true;
                else if (char === "\"")
                    inString = false;
                continue;
            }
            if (char === "\"") {
                inString = true;
            }
            else if (char === "{") {
                depth += 1;
                started = true;
            }
            else if (char === "}") {
                depth -= 1;
                if (started && depth === 0) {
                    const specText = jsonLines.join("\n").trim();
                    const parsed = parseChartSpec(specText);
                    return { spec: parsed.spec, specText, error: parsed.error, endLine: cursor };
                }
            }
        }
    }
    return null;
}

function isLooseChartStart(lines, index) {
    const line = lines[index]?.trim().toLowerCase();
    if (line !== "chart" && line !== "guttenberg-chart")
        return false;
    let cursor = index + 1;
    while (cursor < lines.length && !lines[cursor].trim())
        cursor += 1;
    return lines[cursor]?.trim().startsWith("{") ?? false;
}

function parseChartSpec(specText) {
    try {
        const parsed = JSON.parse(specText);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return { spec: {}, error: "La specification du graphe doit etre un objet JSON." };
        }
        return { spec: normalizeEditableChartSpec(parsed), error: "" };
    }
    catch {
        return { spec: {}, error: "JSON invalide." };
    }
}

function normalizeEditableChartSpec(input) {
    if (!input || typeof input !== "object" || Array.isArray(input))
        return {};
    const spec = input.format === "deepflow-chart-v1"
        ? deepflowToEditableChartSpec(input)
        : isExternalSourceChartSpec(input)
            ? externalSourceToEditableChartSpec(input)
            : { ...input };
    const axes = spec.axes && typeof spec.axes === "object" && !Array.isArray(spec.axes) ? spec.axes : {};
    for (const axis of ["x", "y"]) {
        const axisSpec = axes[axis];
        if (!axisSpec || typeof axisSpec !== "object")
            continue;
        spec[`${axis}_label`] = spec[`${axis}_label`] ?? axisTitle(axisSpec, spec[`${axis}_label`] ?? axis);
        if (axisSpec.min !== undefined)
            spec[`${axis}_min`] = spec[`${axis}_min`] ?? axisSpec.min;
        if (axisSpec.max !== undefined)
            spec[`${axis}_max`] = spec[`${axis}_max`] ?? axisSpec.max;
    }
    if (Array.isArray(spec.referenceLines) && !Array.isArray(spec.reference_lines))
        spec.reference_lines = spec.referenceLines;
    const display = spec.display && typeof spec.display === "object" && !Array.isArray(spec.display) ? spec.display : {};
    if (display.lineWidth !== undefined)
        spec.line_width = spec.line_width ?? display.lineWidth;
    if (display.points !== undefined)
        spec.points = spec.points ?? display.points;
    if (display.showLegend !== undefined)
        spec.legend = spec.legend ?? display.showLegend;
    if (display.showGrid !== undefined)
        spec.grid = spec.grid ?? display.showGrid;
    return spec;
}

function isExternalSourceChartSpec(input) {
    if (!input || typeof input !== "object" || Array.isArray(input))
        return false;
    if (input.dataSource && typeof input.dataSource === "object" && !Array.isArray(input.dataSource))
        return true;
    if (input.data_source && typeof input.data_source === "object" && !Array.isArray(input.data_source))
        return true;
    if (input.xAxis && typeof input.xAxis === "object" && !Array.isArray(input.xAxis))
        return true;
    if (input.yAxis && typeof input.yAxis === "object" && !Array.isArray(input.yAxis))
        return true;
    return Array.isArray(input.series) && input.series.some((item) => item?.field);
}

function externalSourceToEditableChartSpec(input) {
    const dataSource = input.dataSource && typeof input.dataSource === "object" && !Array.isArray(input.dataSource)
        ? input.dataSource
        : input.data_source && typeof input.data_source === "object" && !Array.isArray(input.data_source)
            ? input.data_source
            : {};
    const xAxis = input.xAxis && typeof input.xAxis === "object" && !Array.isArray(input.xAxis) ? input.xAxis : {};
    const yAxis = input.yAxis && typeof input.yAxis === "object" && !Array.isArray(input.yAxis) ? input.yAxis : {};
    const display = input.display && typeof input.display === "object" && !Array.isArray(input.display) ? input.display : {};
    const spec = {
        version: 1,
        format: "guttenberg-chart-v1",
        chart_type: input.chart_type ?? input.type ?? "line",
        title: input.title ?? input.caption ?? "Graphe",
        caption: input.caption ?? input.title ?? "Graphe",
        display,
        data_source: dataSource.source ?? dataSource.file ?? dataSource.path ?? input.data_source,
        source_file_id: dataSource.source_file_id ?? input.source_file_id,
        source_path: dataSource.path ?? input.source_path,
        source_filename: dataSource.file ?? dataSource.filename ?? input.source_filename,
        x_column: xAxis.field ?? xAxis.column ?? input.x_column ?? input.x_key,
        x_transform: xAxis.transform ?? input.x_transform,
        x_label: xAxis.label ?? input.x_label ?? input.x_column ?? "x",
        y_label: yAxis.label ?? input.y_label ?? input.y_column ?? "Valeur",
        legend: display.showLegend ?? input.legend,
        grid: display.showGrid ?? input.grid,
        line_width: display.lineWidth ?? input.line_width,
        series: []
    };
    if (xAxis.min !== undefined || input.x_min !== undefined)
        spec.x_min = xAxis.min ?? input.x_min;
    if (xAxis.max !== undefined || input.x_max !== undefined)
        spec.x_max = xAxis.max ?? input.x_max;
    if (yAxis.min !== undefined || input.y_min !== undefined)
        spec.y_min = yAxis.min ?? input.y_min;
    if (yAxis.max !== undefined || input.y_max !== undefined)
        spec.y_max = yAxis.max ?? input.y_max;
    const series = Array.isArray(input.series) ? input.series : [];
    spec.series = series.map((item, index) => {
        const column = item?.column ?? item?.field ?? item?.y_key ?? item?.y_column;
        return {
            label: item?.label ?? item?.name ?? column ?? `Serie ${index + 1}`,
            column,
            field: column,
            y_key: column,
            transform: item?.transform,
            unit: item?.unit,
            color: item?.color,
            style: item?.style,
            points: Array.isArray(item?.points) ? item.points : []
        };
    });
    if (Array.isArray(input.referenceLines))
        spec.reference_lines = input.referenceLines;
    else if (Array.isArray(input.reference_lines))
        spec.reference_lines = input.reference_lines;
    return Object.fromEntries(Object.entries(spec).filter(([, value]) => value !== undefined && value !== ""));
}

function deepflowToEditableChartSpec(input) {
    const data = input.data && typeof input.data === "object" && !Array.isArray(input.data) ? input.data : {};
    const xSpec = data.x && typeof data.x === "object" && !Array.isArray(data.x) ? data.x : {};
    const axes = input.axes && typeof input.axes === "object" && !Array.isArray(input.axes) ? input.axes : {};
    const xAxis = axes.x && typeof axes.x === "object" ? axes.x : {};
    const yAxis = axes.y && typeof axes.y === "object" ? axes.y : {};
    const spec = {
        version: 1,
        format: "guttenberg-chart-v1",
        chart_type: input.chart_type ?? input.type ?? "line",
        title: input.title ?? input.caption ?? "Graphe",
        caption: input.caption ?? input.title ?? "Graphe",
        axes,
        display: input.display ?? {},
        data_source: data.source ?? input.data_source ?? input.source_file_id ?? input.source_path,
        source_file_id: input.source_file_id ?? data.source_file_id,
        source_path: input.source_path ?? data.source_path,
        source_filename: input.source_filename ?? data.source_filename,
        x_column: xSpec.column ?? input.x_column ?? input.x_key,
        x_transform: xSpec.transform ?? input.x_transform,
        x_label: axisTitle(xAxis, xSpec.label ?? xSpec.column ?? "x"),
        y_label: axisTitle(yAxis, input.y_label ?? input.y_column ?? "Valeur"),
        series: []
    };
    const series = Array.isArray(data.series) ? data.series : Array.isArray(input.series) ? input.series : [];
    spec.series = series.map((item, index) => {
        const column = item?.column ?? item?.y_key ?? item?.y_column;
        return {
            label: item?.label ?? item?.name ?? column ?? `Serie ${index + 1}`,
            column,
            y_key: column,
            unit: item?.unit,
            color: item?.color,
            style: item?.style,
            points: Array.isArray(item?.points) ? item.points : []
        };
    });
    if (Array.isArray(input.referenceLines))
        spec.reference_lines = input.referenceLines;
    else if (Array.isArray(input.reference_lines))
        spec.reference_lines = input.reference_lines;
    return Object.fromEntries(Object.entries(spec).filter(([, value]) => value !== undefined && value !== ""));
}

function axisTitle(axisSpec, fallback) {
    const label = String(axisSpec?.label ?? fallback ?? "").trim() || "Valeur";
    const unit = String(axisSpec?.unit ?? "").trim();
    return unit && !label.includes(unit) ? `${label} (${unit})` : label;
}

function chartSpecForEditor(input) {
    const normalized = normalizeEditableChartSpec(input);
    const series = Array.isArray(normalized.series) ? normalized.series : [];
    const referenceLines = Array.isArray(normalized.reference_lines)
        ? normalized.reference_lines
        : Array.isArray(normalized.referenceLines)
            ? normalized.referenceLines
            : [];
    const chartType = normalizeChartType(normalized.chart_type ?? normalized.type ?? "line");
    const allowCategoricalX = chartAllowsCategoricalX(chartType);
    return cleanChartSpecForSave({
        format: normalized.format ?? "guttenberg-chart-v1",
        version: normalized.version ?? 1,
        chart_type: chartType,
        title: normalized.title ?? normalized.caption ?? "Graphe",
        caption: normalized.caption ?? normalized.title ?? "Graphe",
        data_source: normalized.data_source,
        source_file_id: normalized.source_file_id,
        source_path: normalized.source_path,
        source_filename: normalized.source_filename,
        x_column: normalized.x_column ?? normalized.x_key,
        x_transform: normalized.x_transform,
        x_label: normalized.x_label ?? normalized.x_column ?? "x",
        x_min: normalized.x_min,
        x_max: normalized.x_max,
        y_label: normalized.y_label ?? normalized.y_column ?? "Valeur",
        y_min: normalized.y_min,
        y_max: normalized.y_max,
        y2_label: normalized.y2_label,
        y2_min: normalized.y2_min,
        y2_max: normalized.y2_max,
        legend: normalized.legend,
        grid: normalized.grid,
        points: normalized.points,
        line_width: normalized.line_width ?? normalized.lineWidth,
        display: normalized.display,
        data: normalized.data,
        values: normalized.values,
        x_key: normalized.x_key,
        y_key: normalized.y_key,
        y_keys: normalized.y_keys,
        y_columns: normalized.y_columns,
        reference_lines: referenceLines.map((line) => cleanChartObject({
            axis: line?.axis ?? "y",
            value: optionalNumber(line?.value),
            label: line?.label ?? "",
            style: line?.style ?? "dashed",
            color: line?.color,
        })),
        series: series.map((item, index) => {
            const column = item?.column ?? item?.field ?? item?.y_key ?? item?.y_column ?? "";
            const rawPoints = Array.isArray(item?.points)
                ? item.points
                : Array.isArray(item?.x) && Array.isArray(item?.y)
                    ? item.x.map((xValue, pointIndex) => [xValue, item.y[pointIndex]])
                    : [];
            return cleanChartObject({
                label: item?.label ?? item?.name ?? column ?? `Série ${index + 1}`,
                column,
                field: item?.field ?? column,
                y_key: item?.y_key ?? column,
                transform: item?.transform ?? item?.y_transform,
                unit: item?.unit,
                axis: chartSeriesUsesRightAxis(item) ? "y2" : "",
                color: item?.color,
                style: item?.style,
                drop_first_points: optionalNumber(item?.drop_first_points),
                points: normalizeChartPoints(rawPoints, { allowCategoricalX }),
            });
        }),
    });
}

function cleanChartSpecForSave(value) {
    if (Array.isArray(value))
        return value.map(cleanChartSpecForSave).filter((item) => item !== undefined);
    if (value && typeof value === "object") {
        const result = {};
        Object.entries(value).forEach(([key, item]) => {
            const cleaned = cleanChartSpecForSave(item);
            if (cleaned === undefined)
                return;
            if (typeof cleaned === "string" && cleaned.trim() === "")
                return;
            if (Array.isArray(cleaned) && !cleaned.length && key !== "points" && key !== "series" && key !== "reference_lines")
                return;
            if (cleaned && typeof cleaned === "object" && !Array.isArray(cleaned) && !Object.keys(cleaned).length)
                return;
            result[key] = cleaned;
        });
        return result;
    }
    if (value === undefined || value === null)
        return undefined;
    if (typeof value === "number" && !Number.isFinite(value))
        return undefined;
    return value;
}

function cleanChartObject(value) {
    return cleanChartSpecForSave(value) ?? {};
}

function normalizeChartPoints(points, options = {}) {
    const allowCategoricalX = Boolean(options.allowCategoricalX);
    return points.map((point) => {
        let xRaw;
        let yRaw;
        if (Array.isArray(point)) {
            xRaw = point[0];
            yRaw = point[1];
        }
        else {
            xRaw = point?.x ?? point?.category ?? point?.label ?? point?.name;
            yRaw = point?.y ?? point?.value;
        }
        const y = optionalNumber(yRaw);
        if (!Number.isFinite(y))
            return null;
        const x = optionalNumber(xRaw);
        if (Number.isFinite(x))
            return [x, y];
        const label = String(xRaw ?? "").trim();
        return allowCategoricalX && label ? [label, y] : null;
    }).filter(Boolean);
}

function pointsToText(points, options = {}) {
    if (!Array.isArray(points) || !points.length)
        return "";
    return normalizeChartPoints(points, options)
        .map((point) => `${formatChartPointLabel(point[0])}, ${formatChartNumber(point[1])}`)
        .join("\n");
}

function pointsFromText(value, options = {}) {
    const allowCategoricalX = Boolean(options.allowCategoricalX);
    return String(value ?? "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => parseChartPointLine(line, { allowCategoricalX }))
        .filter(Boolean);
}

function parseChartPointLine(line, options = {}) {
    const allowCategoricalX = Boolean(options.allowCategoricalX);
    if (allowCategoricalX) {
        const categoricalMatch = line.match(/^(.+?)[,;\t]\s*(-?\d+(?:[.,]\d+)?)\s*$/);
        if (categoricalMatch) {
            const label = categoricalMatch[1].trim();
            const value = optionalNumber(categoricalMatch[2]);
            if (label && Number.isFinite(value))
                return [label, value];
        }
    }
    const parts = line.split(/[;\t, ]+/).filter(Boolean);
    const x = optionalNumber(parts[0]);
    const y = optionalNumber(parts[1]);
    return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
}

function formatChartPointLabel(value) {
    return typeof value === "number" ? formatChartNumber(value) : String(value ?? "");
}

function chartAllowsCategoricalX(value) {
    return ["bar", "barh"].includes(normalizeChartType(value));
}

function chartInputValue(value) {
    return value === undefined || value === null ? "" : String(value);
}

function optionalNumber(value) {
    if (value === undefined || value === null || value === "")
        return undefined;
    const parsed = Number(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : undefined;
}

function parseVisualAssetLine(line) {
    const match = line.match(/^\[\[VISUAL_ASSET:?\s*(\{.*\})\s*\]\]$/);
    if (!match)
        return null;
    try {
        const parsed = JSON.parse(match[1]);
        return {
            path: String(parsed.path ?? parsed.figure_path ?? parsed.visual_path ?? ""),
            caption: String(parsed.caption ?? parsed.title ?? "Visuel selectionne")
        };
    }
    catch {
        return { path: "", caption: "Visuel selectionne" };
    }
}

function parseVisualRequestLine(line) {
    const match = line.match(/^\[\[VISUAL_REQUEST:?\s*(.+?)\s*\]\]$/);
    if (!match)
        return null;
    const payload = match[1].trim();
    if (!payload.startsWith("{")) {
        return { title: "Visuel a inserer", description: payload };
    }
    try {
        const parsed = JSON.parse(payload);
        const title = String(parsed.title ?? "Visuel a produire").trim();
        const description = String(parsed.description ?? parsed.rationale ?? "").trim();
        return { title, description };
    }
    catch {
        return { title: "Visuel a produire", description: payload };
    }
}

function isMarkdownTableStart(lines, index) {
    const line = lines[index]?.trim() ?? "";
    const next = lines[index + 1]?.trim() ?? "";
    const separators = splitTableRow(next);
    return isTableRow(line) &&
        index + 1 < lines.length &&
        separators.length > 0 &&
        separators.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function isTableRow(line) {
    const trimmed = line.trim();
    return trimmed.startsWith("|") && trimmed.includes("|", 1);
}

function splitTableRow(line) {
    const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
    const cells = [];
    let current = "";
    for (let index = 0; index < trimmed.length; index += 1) {
        const char = trimmed[index];
        if (char === "\\" && trimmed[index + 1] === "|") {
            current += "|";
            index += 1;
        }
        else if (char === "|") {
            cells.push(current.trim());
            current = "";
        }
        else {
            current += char;
        }
    }
    cells.push(current.trim());
    return cells;
}

function parseTableAlignments(separatorLine, columnCount) {
    const cells = splitTableRow(separatorLine);
    return Array.from({ length: columnCount }, (_, index) => {
        const value = cells[index]?.trim() ?? "---";
        const left = value.startsWith(":");
        const right = value.endsWith(":");
        if (left && right)
            return "center";
        if (right)
            return "right";
        if (left)
            return "left";
        return "default";
    });
}

function normalizeTableBlock(block) {
    const width = Math.max(1, block.headers?.length ?? 0, ...(block.rows ?? []).map((row) => row.length));
    const headers = Array.from({ length: width }, (_, index) => String(block.headers?.[index] ?? ""));
    const rows = (block.rows ?? []).map((row) => Array.from({ length: width }, (_, index) => String(row[index] ?? "")));
    const alignments = Array.from({ length: width }, (_, index) => block.alignments?.[index] ?? "default");
    return { ...block, headers, rows, alignments };
}

function cleanEditableText(value) {
    return value.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

function inlineMarkdownFromEditable(element) {
    if (!element)
        return "";
    function serialize(node) {
        if (node.nodeType === Node.TEXT_NODE)
            return node.textContent ?? "";
        if (node.nodeType !== Node.ELEMENT_NODE)
            return "";
        const tag = node.tagName.toLowerCase();
        if (tag === "br")
            return " ";
        const content = Array.from(node.childNodes).map(serialize).join("");
        if (!content)
            return "";
        if (tag === "strong" || tag === "b")
            return `**${content}**`;
        if (tag === "em" || tag === "i")
            return `*${content}*`;
        if (tag === "a") {
            const href = String(node.getAttribute("href") || "").trim();
            return href ? (content === href ? href : `[${content}](${href})`) : content;
        }
        const fontWeight = String(node.style?.fontWeight || "").toLowerCase();
        const fontStyle = String(node.style?.fontStyle || "").toLowerCase();
        const bold = fontWeight === "bold" || Number.parseInt(fontWeight, 10) >= 600;
        const italic = fontStyle === "italic" || fontStyle === "oblique";
        if (bold && italic)
            return `***${content}***`;
        if (bold)
            return `**${content}**`;
        if (italic)
            return `*${content}*`;
        return content;
    }
    return cleanEditableText(Array.from(element.childNodes).map(serialize).join(""));
}

function capitalizeCaption(value) {
    const text = cleanEditableText(String(value ?? ""));
    const firstLetter = text.search(/\p{L}/u);
    if (firstLetter < 0)
        return text;
    return `${text.slice(0, firstLetter)}${text[firstLetter].toLocaleUpperCase("fr-FR")}${text.slice(firstLetter + 1)}`;
}

function blockToMarkdown(block, value) {
    const clean = cleanEditableText(value);
    if (!clean && block.type === "h1")
        return "# Partie sans titre";
    if (!clean && ["h2", "bullet", "paragraph"].includes(block.type))
        return "";
    if (block.type === "h1")
        return `# ${clean}`;
    if (block.type === "h2")
        return `## ${clean}`;
    if (block.type === "bullet")
        return `- ${clean}`;
    if (block.type === "paragraph")
        return clean;
    return block.raw;
}

function imageBlockToMarkdown(block, caption) {
    const clean = capitalizeCaption(caption);
    const alt = capitalizeCaption(block.alt || clean);
    const lines = [`![${alt}](${block.path})`];
    if (clean)
        lines.push(`*${clean}*`);
    return lines.join("\n");
}

function chartBlockToMarkdown(spec) {
    return `\`\`\`chart\n${JSON.stringify(spec, null, 2)}\n\`\`\``;
}

function orderedListBlockToMarkdown(block, itemIndex, value) {
    const items = block.items
        .map((item, index) => {
        if (index === itemIndex)
            return cleanEditableText(value);
        const text = item.title ? `**${item.title}**${item.body ? ` ${item.body}` : ""}` : item.body;
        return String(text || "").trim();
    })
        .filter(Boolean);
    return items
        .map((item, index) => `${index + 1}. ${item}`)
        .join("\n");
}

function tableBlockToMarkdown(block, rowIndex, cellIndex, value) {
    const normalized = normalizeTableBlock(block);
    const headers = [...normalized.headers];
    const rows = normalized.rows.map((row) => [...row]);
    if (rowIndex < 0)
        headers[cellIndex] = cleanEditableText(value);
    else if (rows[rowIndex])
        rows[rowIndex][cellIndex] = cleanEditableText(value);
    return serializeMarkdownTable({ ...normalized, headers, rows });
}

function tableStructureToMarkdown(block, operation) {
    const normalized = normalizeTableBlock(block);
    const headers = [...normalized.headers];
    const rows = normalized.rows.map((row) => [...row]);
    const alignments = [...normalized.alignments];
    if (operation.type === "add_row") {
        const insertAt = Math.max(0, Math.min(rows.length, operation.rowIndex + (operation.position === "after" ? 1 : 0)));
        rows.splice(insertAt, 0, headers.map(() => ""));
    }
    else if (operation.type === "delete_row") {
        if (operation.rowIndex >= 0 && operation.rowIndex < rows.length)
            rows.splice(operation.rowIndex, 1);
    }
    else if (operation.type === "add_column") {
        const insertAt = Math.max(0, Math.min(headers.length, operation.columnIndex + (operation.position === "right" ? 1 : 0)));
        headers.splice(insertAt, 0, "Nouvelle colonne");
        alignments.splice(insertAt, 0, "default");
        rows.forEach((row) => row.splice(insertAt, 0, ""));
    }
    else if (operation.type === "delete_column" && headers.length > 1) {
        const removeAt = Math.max(0, Math.min(headers.length - 1, operation.columnIndex));
        headers.splice(removeAt, 1);
        alignments.splice(removeAt, 1);
        rows.forEach((row) => row.splice(removeAt, 1));
    }
    return serializeMarkdownTable({ ...normalized, headers, rows, alignments });
}

function serializeMarkdownTable(block) {
    const normalized = normalizeTableBlock(block);
    const separator = normalized.alignments.map((alignment) => {
        if (alignment === "left")
            return ":---";
        if (alignment === "right")
            return "---:";
        if (alignment === "center")
            return ":---:";
        return "---";
    });
    return [normalized.headers, separator, ...normalized.rows].map(markdownTableRow).join("\n");
}

function markdownTableRow(cells) {
    return `| ${cells.map((cell) => String(cell ?? "").replace(/\\?\|/g, "\\|")).join(" | ")} |`;
}

function replaceMarkdownBlockByIndex(markdown, blockIndex, nextBlockMarkdown) {
    const blocks = parseMarkdownBlocks(markdown);
    const block = blocks[blockIndex];
    if (!block)
        return markdown;
    const lines = markdown.split("\n");
    const trimmedReplacement = nextBlockMarkdown.trim();
    const replacement = trimmedReplacement ? trimmedReplacement.split("\n") : [];
    const nextLines = [...lines.slice(0, block.startLine), ...replacement, ...lines.slice(block.endLine + 1)];
    return `${nextLines.join("\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;
}

function insertMarkdownBlockAfterIndex(markdown, blockIndex, blockMarkdown) {
    const blocks = parseMarkdownBlocks(markdown);
    const block = blocks[blockIndex];
    const clean = String(blockMarkdown ?? "").trim();
    if (!block || !clean)
        return markdown;
    const lines = markdown.split("\n");
    const insertAt = block.endLine + 1;
    const nextLines = [
        ...lines.slice(0, insertAt),
        "",
        ...clean.split("\n"),
        "",
        ...lines.slice(insertAt),
    ];
    return `${nextLines.join("\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;
}

function blockDeleteLabel(block) {
    if (block.type === "h1")
        return "ce titre";
    if (block.type === "h2")
        return "ce sous-titre";
    if (block.type === "image")
        return "cette image";
    if (block.type === "table")
        return "ce tableau";
    if (block.type === "ordered_list")
        return "cette liste";
    if (block.type === "bullet")
        return "cette puce";
    if (block.type === "chart")
        return "ce graphe";
    if (block.type === "blockquote")
        return "cette citation";
    return "ce bloc";
}

function blockVersionHistoryFor(versions, blockIndex, block) {
    const history = [];
    const used = new Set();
    let expectedFingerprint = block.fingerprint;
    let expectedIndex = blockIndex;
    for (const version of versions) {
        const fingerprintToMatch = expectedFingerprint;
        const indexToMatch = expectedIndex;
        let match = version.changed_blocks.find((item) => item.current_fingerprint === fingerprintToMatch &&
            item.change_type !== "removed" &&
            !used.has(`${version.id}:${item.id}`));
        if (!match && history.length === 0) {
            match = version.changed_blocks.find((item) => item.block_index === indexToMatch &&
                item.change_type !== "removed" &&
                !used.has(`${version.id}:${item.id}`));
        }
        if (!match)
            continue;
        history.push({ version, block: match });
        used.add(`${version.id}:${match.id}`);
        if (!match.previous_fingerprint)
            break;
        expectedFingerprint = match.previous_fingerprint;
        expectedIndex = match.block_index;
    }
    return history;
}

function versionTooltip(version) {
    const origin = version.origin ? ` Origine: ${version.origin}` : "";
    return `${sourceLabel(version.source)} - ${version.action || "Modification"} - ${formatDateTime(version.created_at)}.${origin}`;
}

function versionActionLabel(action) {
    if (action === "manual-edit")
        return "Edition manuelle";
    if (action === "restore-block")
        return "Restauration d'un bloc";
    if (action === "restore-section")
        return "Restauration de section";
    if (action === "revise-section")
        return "Revision IA";
    if (action === "writer-agent")
        return "Redaction IA";
    if (action === "comment-suggestion-accepted")
        return "Suggestion acceptee";
    return action || "Modification";
}

function versionBlockLabel(block) {
    return `Bloc ${block.block_index + 1} - ${changeTypeLabel(block.change_type)}`;
}

function changeTypeLabel(changeType) {
    if (changeType === "added")
        return "ajoute";
    if (changeType === "removed")
        return "retire";
    return "modifie";
}

function sourceLabel(source) {
    if (source === "ai")
        return "IA";
    if (source === "user")
        return "Utilisateur";
    return "Systeme";
}

function formatDateTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return "";
    return date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export {
    CHART_SERIES_COLORS,
    ChartAxes,
    ChartBars,
    ChartColorField,
    ChartEditor,
    ChartLegend,
    ChartLines,
    ChartNumberField,
    ChartReferenceLines,
    ChartSelectField,
    ChartTextField,
    axisRange,
    axisTitle,
    blockComparableText,
    blockDeleteLabel,
    blockToMarkdown,
    blockVersionHistoryFor,
    buildChartPreview,
    capitalizeCaption,
    changeTypeLabel,
    chartAllowsCategoricalX,
    chartBarPointsFromSeries,
    chartBarsFromSeries,
    chartBlockToMarkdown,
    chartColorOrDefault,
    chartCommentTargetText,
    chartHasLocalData,
    chartInputValue,
    chartNeedsRemoteRender,
    chartPointsFromSeries,
    chartRenderCache,
    chartRenderClientVersion,
    chartRenderVersion,
    chartSeriesUsesRightAxis,
    chartSourcePath,
    chartSpecForEditor,
    chartTitle,
    cleanChartObject,
    cleanChartSpecForSave,
    cleanEditableText,
    cleanMermaidLabel,
    cssEscape,
    deepflowToEditableChartSpec,
    defaultChartSeriesColor,
    externalSourceToEditableChartSpec,
    extractChartBars,
    extractChartReferenceLines,
    extractChartSeries,
    filterChartSeriesToRange,
    formatChartNumber,
    formatChartPointLabel,
    formatDateTime,
    imageBlockToMarkdown,
    inlineMarkdownFromEditable,
    inputQueueCommentLabel,
    insertMarkdownBlockAfterIndex,
    isBlockBoundary,
    isExternalSourceChartSpec,
    isLooseChartStart,
    isMarkdownTableStart,
    isOrderedListItem,
    isTableRow,
    layoutMermaidNodes,
    markdownTableRow,
    normalizeChartPoints,
    normalizeChartType,
    normalizeEditableChartSpec,
    normalizeTableBlock,
    numeric,
    optionalNumber,
    orderedListBlockToMarkdown,
    parseChartLine,
    parseChartPointLine,
    parseChartSpec,
    parseLooseChartBlock,
    parseMarkdownBlocks,
    parseMermaidFlowchart,
    parseMermaidNode,
    parseTableAlignments,
    parseVisualAssetLine,
    parseVisualRequestLine,
    pointsFromText,
    pointsToText,
    replaceMarkdownBlockByIndex,
    serializeMarkdownTable,
    sourceLabel,
    splitTableRow,
    tableBlockToMarkdown,
    tableStructureToMarkdown,
    versionActionLabel,
    versionBlockLabel,
    versionTooltip,
    withBlockMeta,
    wrapMermaidLabel
};
